import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { debounce } from 'lodash';
import useConversations from './useConversations';
import { applyConversationClientSideFilters } from '../Conversations/conversationFilterUtils';

/**
 * Unified hook to manage conversation pagination, filtering, search, and sorting
 * Consolidates logic from useConversationPagination, useConversationFiltering, and useInitialConversationLoad
 * 
 * @param {Object} params - Configuration parameters
 * @param {Array} params.conversations - Array of conversations
 * @param {string} params.selectedAgent - Currently selected agent ID
 * @param {Function} params.setConversations - Function to update conversations list
 * @param {Function} params.onSelectConversation - Callback to select a conversation
 * @returns {Object} All pagination, filtering, and sorting state and handlers
 */
const useConversationManager = ({
    conversations,
    selectedChannel,
    setConversations,
    onSelectConversation,
    skipInitialLoad = false,
}) => {
    // ===== Pagination State =====
    const [nextCursor, setNextCursor] = useState(null);
    const [hasNextPage, setHasNextPage] = useState(true);
    const [prevCursor, setPrevCursor] = useState(null); // NEW: For upward scroll
    const [hasPrevPage, setHasPrevPage] = useState(false); // NEW: For upward scroll
    const [loading, setLoading] = useState(false);
    const [loadingTop, setLoadingTop] = useState(false); // NEW: Separate loading for top
    const perPage = 25;

    // ===== Filtering State =====
    const [searchText, setSearchText] = useState('');
    const [chipFilters, setChipFilters] = useState([]);
    const [normalizedChipFilters, setNormalizedChipFilters] = useState({});
    const [sortBy, setSortBy] = useState('updated_time');
    const [sortDirection, setSortDirection] = useState('desc');

    // ===== Request Cancellation =====
    // Track the current request to cancel it if a new request is triggered (search, filter, channel change, etc)
    const apiAbortControllerRef = useRef(null);

    const getFreshSignal = useCallback(() => {
        if (apiAbortControllerRef.current) {
            apiAbortControllerRef.current.abort();
        }
        apiAbortControllerRef.current = new AbortController();
        return apiAbortControllerRef.current.signal;
    }, []);

    const { fetchConversations } = useConversations();

    // ===== Unified Fetch Function =====
    /**
     * Single function to fetch conversations with all parameters
     * Eliminates duplicate API calls from separate hooks
     */
    const fetchWithCurrentParams = useCallback((overrides = {}) => {
        if (!selectedChannel) return;

        const params = {
            perPage,
            selectedChannel,
            setConversations,
            setHasNextPage,
            setHasPrevPage, // NEW: For upward pagination
            setLoading: overrides.shouldPrepend ? setLoadingTop : setLoading, // Use different loading state
            search_string: searchText,
            filters: normalizedChipFilters || {},
            sort_by: sortBy,
            sort_direction: sortDirection,
            shouldAppend: false,
            shouldPrepend: false, // NEW: For upward pagination
            // Cursor pagination params
            after: null,
            before: null, // NEW: For upward pagination
            setNextCursor,
            setPrevCursor, // NEW: For upward pagination
            signal: getFreshSignal(), // Use fresh signal for every request
            ...overrides
        };

        return fetchConversations(params);
    }, [
        perPage,
        selectedChannel,
        setConversations,
        searchText,
        normalizedChipFilters, // Latest filters are included, but debounce logic might be better handled directly
        sortBy,
        sortDirection,
        fetchConversations,
        getFreshSignal
    ]);

    // ===== Pagination Handlers =====
    const handleScroll = useCallback((e) => {
        const { scrollHeight, scrollTop, clientHeight } = e.currentTarget;

        // TOP: When user scrolls to top, load older conversations
        if (scrollTop === 0) {
            console.log('Scroll: At top', { hasPrevPage, loadingTop, prevCursor });
            if (hasPrevPage && !loadingTop) {
                console.log('Scroll: Fetching older conversations with before cursor');
                fetchWithCurrentParams({
                    before: prevCursor,
                    shouldPrepend: true
                });
            }
        }

        // BOTTOM: When user reaches the bottom, load newer conversations
        if (scrollTop + clientHeight >= scrollHeight - 2 && hasNextPage && !loading) {
            fetchWithCurrentParams({
                after: nextCursor,
                shouldAppend: true
            });
        }
    }, [hasNextPage, hasPrevPage, loading, loadingTop, nextCursor, prevCursor, fetchWithCurrentParams]);

    // Reload the first page (the latest conversations for descending sort)
    // instead of only scrolling to the top of a deep-linked/paginated slice.
    const fetchLatestConversations = useCallback(() => (
        fetchWithCurrentParams({
            after: null,
            before: null,
            shouldAppend: false,
            shouldPrepend: false
        })
    ), [fetchWithCurrentParams]);

    const resetPagination = useCallback(() => {
        setNextCursor(null);
        setHasNextPage(true);
        setPrevCursor(null); // NEW: Reset upward cursor
        setHasPrevPage(false); // NEW: Reset upward page flag
    }, []);

    // ===== Filtering Handlers =====

    // Track latest values in refs to avoid stale closures in debounced function
    const latestFiltersRef = useRef(normalizedChipFilters);
    const latestSortByRef = useRef(sortBy);
    const latestSortDirectionRef = useRef(sortDirection);

    useEffect(() => {
        latestFiltersRef.current = normalizedChipFilters;
        latestSortByRef.current = sortBy;
        latestSortDirectionRef.current = sortDirection;
    }, [normalizedChipFilters, sortBy, sortDirection]);

    // Helper to clear list and show loading state
    const clearAndShowLoading = useCallback(() => {
        setConversations([]);
        setHasNextPage(true);
        setLoading(true);
    }, [setConversations]);

    // Create stable debounced search function
    const debouncedSearchRef = useRef(null);
    const debouncedFiltersRef = useRef(null);

    // Track latest searchText in a ref to avoid stale closures in debounced filter function
    const latestSearchTextRef = useRef(searchText);
    useEffect(() => {
        latestSearchTextRef.current = searchText;
    }, [searchText]);

    useEffect(() => {
        debouncedSearchRef.current = debounce((searchValue, channelId) => {
            // Reset pagination
            setNextCursor(null);
            setHasNextPage(true);

            // Fetch conversations with the search value and latest filter/sort state
            fetchConversations({
                perPage,
                selectedChannel: channelId,
                setConversations,
                setHasNextPage,
                setLoading,
                search_string: searchValue,
                filters: latestFiltersRef.current,
                sort_by: latestSortByRef.current,
                sort_direction: latestSortDirectionRef.current,
                shouldAppend: false,
                after: null,
                setNextCursor,
                signal: getFreshSignal()
            });
        }, 500);

        debouncedFiltersRef.current = debounce((filtersPayload, channelId) => {
            // Reset pagination
            setNextCursor(null);
            setHasNextPage(true);

            // Fetch conversations with the new filters
            // Use ref to access latest search text without causing re-creation of debounce
            fetchConversations({
                perPage,
                selectedChannel: channelId,
                setConversations,
                setHasNextPage,
                setLoading,
                search_string: latestSearchTextRef.current || '',
                filters: filtersPayload,
                sort_by: latestSortByRef.current,
                sort_direction: latestSortDirectionRef.current,
                shouldAppend: false,
                after: null,
                setNextCursor,
                signal: getFreshSignal() // Add missing signal
            });
        }, 500);

        // Cleanup on unmount
        return () => {
            if (debouncedSearchRef.current) {
                debouncedSearchRef.current.cancel();
            }
            if (debouncedFiltersRef.current) {
                debouncedFiltersRef.current.cancel();
            }
        };
    // searchText is tracked via latestSearchTextRef to avoid re-creating debounce on every keystroke
    }, [perPage, fetchConversations, setConversations]);

    const handleSearchChange = useCallback((e) => {
        const search = e.target.value;
        setSearchText(search);
        clearAndShowLoading();
        debouncedSearchRef.current(search, selectedChannel);
    }, [selectedChannel, clearAndShowLoading]);

    const handleFiltersChange = useCallback((nextFilters, normalized) => {
        setChipFilters(nextFilters);
        setNormalizedChipFilters(normalized);
        resetPagination();
        clearAndShowLoading();

        debouncedFiltersRef.current(normalized || {}, selectedChannel);
    }, [resetPagination, clearAndShowLoading, selectedChannel]);

    const handleSortChange = useCallback(({ sort_by, sort_direction }) => {
        setSortBy(sort_by || 'updated_time');
        setSortDirection(sort_direction || 'desc');
        resetPagination();
        clearAndShowLoading();

        fetchWithCurrentParams({
            sort_by: sort_by || 'updated_time',
            sort_direction: sort_direction || 'desc',
            shouldAppend: false
        });
    }, [resetPagination, clearAndShowLoading, fetchWithCurrentParams]);

    // ===== External State Setter =====
    // Allow external components (like deep link logic) to update pagination state
    const setPaginationState = useCallback(({ nextCursor, hasNextPage, prevCursor, hasPrevPage }) => {
        if (nextCursor !== undefined) setNextCursor(nextCursor);
        if (hasNextPage !== undefined) setHasNextPage(hasNextPage);
        if (prevCursor !== undefined) setPrevCursor(prevCursor);
        if (hasPrevPage !== undefined) setHasPrevPage(hasPrevPage);
    }, []);

    // ===== Initial Load Effect =====
    /**
     * Load conversations when channel changes
     * Clears selection, resets pagination, and fetches fresh data
     */
    useEffect(() => {
        if (selectedChannel && !skipInitialLoad) {
            // Clear currently selected conversation when channel changes
            if (onSelectConversation) {
                onSelectConversation(null);
            }
            resetPagination();
            setConversations([]);
            setLoading(true);
            fetchConversations({
                perPage,
                selectedChannel,
                setConversations,
                setHasNextPage,
                setHasPrevPage, // NEW: Enable upward pagination
                setLoading,
                shouldAppend: false,
                shouldPrepend: false, // NEW: Not prepending on initial load
                filters: {},
                sort_by: sortBy,
                sort_direction: sortDirection,
                after: null,
                before: null, // NEW: No before cursor on initial load
                setNextCursor,
                setPrevCursor, // NEW: Enable upward pagination
                signal: getFreshSignal(), // Abort any ongoing fetches for previous channels
            });
        }

        // Cleanup: cancel pending search and debounce on unmount or channel change
        return () => {
            if (apiAbortControllerRef.current) {
                apiAbortControllerRef.current.abort();
            }
            // Cancel any pending debounced calls
            if (debouncedSearchRef.current) {
                debouncedSearchRef.current.cancel();
            }
            if (debouncedFiltersRef.current) {
                debouncedFiltersRef.current.cancel();
            }
        };
        // Only depend on selectedChannel - sort/filter changes are handled by their respective handlers
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedChannel]);

    // ===== Client-Side Filtering =====
    /**
     * Apply client-side filters (search and chip filters)
     * Server returns sorted data, we just filter locally
     */
    const sortedConversations = useMemo(() => {
        if (!conversations || !Array.isArray(conversations)) return [];

        const filtered = applyConversationClientSideFilters({
            conversations,
            searchText,
            normalizedFilters: normalizedChipFilters,
        });

        // Apply client-side sorting based on user preferences or default fallback
        return [...filtered].sort((a, b) => {
            const fieldA = a[sortBy];
            const fieldB = b[sortBy];

            if (fieldA === fieldB) return 0;
            if (fieldA == null) return sortDirection === 'desc' ? 1 : -1;
            if (fieldB == null) return sortDirection === 'desc' ? -1 : 1;

            const isDate = String(sortBy).includes('time') || String(sortBy).includes('date') || String(sortBy).includes('_at');
            const valA = isDate ? new Date(fieldA).getTime() : fieldA;
            const valB = isDate ? new Date(fieldB).getTime() : fieldB;
            
            // If invalid date, fallback to original primitive
            const finalA = (isDate && isNaN(valA)) ? fieldA : valA;
            const finalB = (isDate && isNaN(valB)) ? fieldB : valB;

            if (finalA < finalB) return sortDirection === 'desc' ? 1 : -1;
            if (finalA > finalB) return sortDirection === 'desc' ? -1 : 1;
            return 0;
        });
    }, [conversations, searchText, normalizedChipFilters, sortBy, sortDirection]);

    // ===== Return All State and Handlers =====
    return {
        // Pagination
        hasNextPage,
        hasPrevPage, // NEW: For upward scroll
        loading,
        loadingTop, // NEW: Separate loading indicator for top
        handleScroll,
        fetchLatestConversations,

        // Filtering and Sorting
        chipFilters,
        sortBy,
        sortDirection,
        sortedConversations,
        handleSearchChange,
        handleFiltersChange,
        handleSortChange,
        setPaginationState // NEW: Exposure for manual updates
    };
};

export default useConversationManager;
